import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useIdioma } from '@vigilancia/i18n';
import { crearPilaDeEmergentes, type PilaDeEmergentes } from '@vigilancia/ui/emergentes.js';

import { leerElFlujo } from './flujo';
import { presentar } from './registro';
import {
  servicioDeNotificaciones,
  vigilarLaSesion,
  type Cuenta,
  type Notificacion,
  type ServicioDeNotificaciones,
} from './servicio';
import { haceCuanto } from './texto';
import {
  crearTiempoReal,
  NOMBRE_DEL_CANAL,
  type DependenciasDelTiempoReal,
  type ModoDeTiempoReal,
  type TiempoReal,
} from './tiempoReal';

/**
 * LAS NOTIFICACIONES DE LA PÁGINA · 🆕 5/10/2026, segunda vuelta (ADR-0021).
 *
 * Lo que comparten la campana, la página y los emergentes: el servicio, la cuenta al
 * día —el tiempo real—, la pila de emergentes y la regla de cuándo sale uno.
 *
 * ⚠️ **TODO LO DEL NAVEGADOR SE CREA EN UN EFECTO**: el portal se pinta en el
 * servidor, y allí no hay flujo, ni canal, ni pila.
 *
 * **CUÁNDO SALE UN EMERGENTE (D16)**: cuando sube «sin leer» con la página a la vista
 * y el desplegable cerrado —con él abierto, lo nuevo entra en la lista—. Uno por
 * notificación hasta tres; más, uno solo que las cuenta. **Lo propio sólo con
 * «Deshacer»**, y lo que sube por un gesto propio —deshacer, marcar como no leída—
 * no se anuncia como nuevo.
 *
 * **SI LA SESIÓN CADUCA** —un 401 en cualquier llamada, o el tiempo real que calla en
 * esta pestaña o en otra—, `caducada`: la campana y la página lo dicen como lo que es,
 * con el camino para volver a entrar, y no como un fallo que se arregla reintentando.
 */

/** Más de estas a la vez se anuncian en un solo emergente. */
const UNA_A_UNA = 3;

interface Desplegable {
  readonly abierto: () => boolean;
  readonly abrir: () => void;
}

export interface Notificaciones {
  readonly servicio: ServicioDeNotificaciones;
  readonly cuenta: Cuenta | null;
  /** La sesión caducó: nada más se pregunta, y se dice cómo volver a entrar. */
  readonly caducada: boolean;
  /** Adónde lleva «Ver todas». */
  readonly pagina: string;
  /** Tras un gesto propio: la cuenta de ahora, también para las demás pestañas. */
  readonly refrescar: () => void;
  /** Cada vez que cambia la cuenta: para volver a leer lo que se enseña. */
  readonly alCambiar: (oyente: (cuenta: Cuenta, antes: Cuenta | null) => void) => () => void;
  /** La pila de la página; `null` en el servidor o antes de montarse. */
  readonly emergentes: () => PilaDeEmergentes | null;
  /** La campana se presenta: si su desplegable está abierto, y cómo abrirlo. */
  readonly registrarDesplegable: (desplegable: Desplegable) => () => void;
  /** Lo que un gesto propio va a volver a poner sin leer: no es nada nuevo. */
  readonly contarComoPropias: (n: number) => void;
  /** Abre una notificación que lleva a otra página: la marca leída y navega. */
  readonly abrir: (notificacion: Notificacion, enlace: string) => void;
  /**
   * 🆕 6/10/2026 · Si quien mira puede abrir `/admin/accesos`: entonces una petición
   * de acceso lleva a darlo allí. La página la da su RBAC, como cualquier otra.
   */
  readonly administraAccesos: boolean;
}

const Contexto = createContext<Notificaciones | null>(null);

/** Lo del navegador que necesita el tiempo real. */
function delNavegador(
  apiUrl: string,
  servicio: ServicioDeNotificaciones,
): DependenciasDelTiempoReal {
  const base = apiUrl.replace(/\/+$/u, '');
  return {
    cuenta: () => servicio.cuenta(),
    leerElFlujo: (senal, alEvento) =>
      leerElFlujo({ url: `${base}/notificaciones/eventos`, senal, alEvento }),
    pagina: {
      visible: () => document.visibilityState === 'visible',
      alCambiar: (oyente) => {
        document.addEventListener('visibilitychange', oyente);
        return () => {
          document.removeEventListener('visibilitychange', oyente);
        };
      },
    },
    reloj: {
      setTimeout: (fn, ms) => window.setTimeout(fn, ms),
      clearTimeout: (id) => {
        window.clearTimeout(id as number);
      },
      ahora: () => Date.now(),
    },
    azar: Math.random,
    cerrojos: 'locks' in navigator ? navigator.locks : undefined,
    canal:
      typeof BroadcastChannel === 'undefined'
        ? undefined
        : () => new BroadcastChannel(NOMBRE_DEL_CANAL),
    idDePestana: crypto.randomUUID(),
  };
}

interface Props {
  readonly apiUrl: string;
  readonly modo: ModoDeTiempoReal;
  readonly pagina: string;
  /** Sólo para las pruebas: el de verdad sale de `apiUrl`. */
  readonly servicio?: ServicioDeNotificaciones;
  /** Sólo para las pruebas: el de verdad abre el flujo de la API. */
  readonly tiempoReal?: (servicio: ServicioDeNotificaciones) => TiempoReal;
  /** Sólo para las pruebas: el de verdad cambia de página. */
  readonly navegar?: (enlace: string) => void;
  /** Si quien mira puede abrir `/admin/accesos` (6/10/2026). */
  readonly administraAccesos?: boolean;
  readonly children: ReactNode;
}

const cambiarDePagina = (enlace: string): void => {
  window.location.assign(enlace);
};

export function ProveedorDeNotificaciones({
  apiUrl,
  modo,
  pagina,
  servicio: inyectado,
  tiempoReal: crear,
  navegar = cambiarDePagina,
  administraAccesos = false,
  children,
}: Props) {
  const { t, idioma } = useIdioma();
  const [cuenta, setCuenta] = useState<Cuenta | null>(null);
  const [caducada, setCaducada] = useState(false);
  const alCaducar = useCallback(() => {
    setCaducada(true);
  }, []);
  const servicio = useMemo(
    () => vigilarLaSesion(inyectado ?? servicioDeNotificaciones(apiUrl), alCaducar),
    [inyectado, apiUrl, alCaducar],
  );
  const tiempo = useRef<TiempoReal | null>(null);
  const pila = useRef<PilaDeEmergentes | null>(null);
  const desplegable = useRef<Desplegable | null>(null);
  const oyentes = useRef(new Set<(cuenta: Cuenta, antes: Cuenta | null) => void>());
  /** Las que ya salieron en un emergente: no salen dos veces. */
  const avisadas = useRef(new Set<string>());
  /** Cuántas de las próximas que suban son de un gesto propio. */
  const propias = useRef(0);
  // Los textos de ahora, para lo que corre fuera del pintado.
  const textos = useRef({ t, idioma });
  useEffect(() => {
    textos.current = { t, idioma };
  }, [t, idioma]);

  const abrir = useCallback(
    (notificacion: Notificacion, enlace: string) => {
      if (!notificacion.leida) {
        servicio
          .marcar([notificacion.id], { leida: true }, { alSalir: true })
          .catch(() => undefined);
      }
      navegar(enlace);
    },
    [servicio, navegar],
  );

  // La pila de emergentes: una por página, con sus textos.
  useEffect(() => {
    const creada = crearPilaDeEmergentes({
      textos: {
        region: t('cascaron.notificaciones.emergentes.region'),
        cerrar: t('cascaron.notificaciones.emergentes.cerrar'),
      },
    });
    pila.current = creada;
    return () => {
      creada.destruir();
      pila.current = null;
    };
  }, [t]);

  useEffect(() => {
    /** D16: lo que llegó, en emergentes, si se está mirando y el desplegable está cerrado. */
    const avisar = async (nueva: Cuenta, antes: Cuenta | null): Promise<void> => {
      if (antes === null || nueva.sinLeer <= antes.sinLeer) return;
      let llegaron = nueva.sinLeer - antes.sinLeer;
      const suyas = Math.min(propias.current, llegaron);
      propias.current -= suyas;
      llegaron -= suyas;
      const enPila = pila.current;
      if (llegaron <= 0 || enPila === null) return;
      if (document.visibilityState !== 'visible' || desplegable.current?.abierto() === true) return;
      const { t: traducir, idioma: lengua } = textos.current;
      const verlas = (): void => desplegable.current?.abrir();
      if (llegaron > UNA_A_UNA) {
        enPila.mostrar({
          familia: 'info',
          glifo: 'i-campana',
          principal: [{ fuerte: traducir('cascaron.notificaciones.llegaron', { n: llegaron }) }],
          accion: { texto: traducir('cascaron.notificaciones.verlas'), alPulsar: verlas },
        });
        return;
      }
      let leidas: readonly Notificacion[];
      try {
        leidas = (await servicio.pagina({ vista: 'sin-leer', limite: llegaron })).notificaciones;
      } catch {
        return;
      }
      const ahora = new Date();
      // La más antigua primero: la última que llega queda abajo, la más cerca.
      for (const notificacion of [...leidas].reverse()) {
        if (avisadas.current.has(notificacion.id)) continue;
        avisadas.current.add(notificacion.id);
        const { familia, glifo, principal, contexto, enlace } = presentar(
          notificacion,
          traducir,
          lengua,
        );
        enPila.mostrar({
          familia,
          glifo,
          principal,
          contexto: traducir('cascaron.notificaciones.contextoYCuando', {
            contexto,
            cuando: haceCuanto(notificacion.momento, ahora, lengua),
          }),
          accion:
            enlace === null
              ? { texto: traducir('cascaron.notificaciones.ver'), alPulsar: verlas }
              : {
                  texto: traducir('cascaron.notificaciones.abrir'),
                  alPulsar: () => {
                    abrir(notificacion, enlace);
                  },
                },
        });
      }
    };

    const creado = crear?.(servicio) ?? crearTiempoReal(delNavegador(apiUrl, servicio), modo);
    tiempo.current = creado;
    const baja = creado.suscribir((nueva, antes) => {
      setCuenta(nueva);
      for (const oyente of [...oyentes.current]) oyente(nueva, antes);
      void avisar(nueva, antes);
    });
    const bajaDeLaSesion = creado.alCaducar(alCaducar);
    return () => {
      baja();
      bajaDeLaSesion();
      creado.detener();
      tiempo.current = null;
    };
  }, [apiUrl, servicio, modo, crear, abrir, alCaducar]);

  // Lo que no cambia con la cuenta no cambia de identidad: quien se suscribe con ello
  // no se vuelve a suscribir cada vez que llega una.
  const refrescar = useCallback(() => {
    void tiempo.current?.refrescar();
  }, []);
  const alCambiar = useCallback((oyente: (cuenta: Cuenta, antes: Cuenta | null) => void) => {
    oyentes.current.add(oyente);
    return () => {
      oyentes.current.delete(oyente);
    };
  }, []);
  const emergentes = useCallback(() => pila.current, []);
  const registrarDesplegable = useCallback((registrado: Desplegable) => {
    desplegable.current = registrado;
    return () => {
      if (desplegable.current === registrado) desplegable.current = null;
    };
  }, []);
  const contarComoPropias = useCallback((n: number) => {
    propias.current += Math.max(0, n);
  }, []);

  const valor = useMemo<Notificaciones>(
    () => ({
      servicio,
      cuenta,
      caducada,
      pagina,
      refrescar,
      alCambiar,
      emergentes,
      registrarDesplegable,
      contarComoPropias,
      abrir,
      administraAccesos,
    }),
    [
      servicio,
      cuenta,
      caducada,
      pagina,
      refrescar,
      alCambiar,
      emergentes,
      registrarDesplegable,
      contarComoPropias,
      abrir,
      administraAccesos,
    ],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

/** Las notificaciones de la página. Fuera de su proveedor no hay ninguna. */
export function useNotificaciones(): Notificaciones | null {
  return useContext(Contexto);
}
